import { Polyline } from "@react-google-maps/api";
import lodash from "lodash";
import React, { FunctionComponent, useCallback, useContext } from "react";
import { EventType, RouteStopFragment } from "../../../api/nachfahrbarkeit-api/generated";
import { CustomMap, IMarker, MarkerType } from "../../../components/maps/custom-map";
import { RouteDetailsMapInfoWindow } from "./route-details-map-info-window";
import { EditRouteStopsContext } from "./edit-route-stops.context";

interface LatLong {
  lat: number;
  lng: number;
}

interface PolygonlineCoords {
  coords: LatLong[];
  color: PolygonlineColor;
}

enum PolygonlineColor {
  BLACK = "#000000",
  RED = "#ff0000",
}

const fallBackMarker: IMarker[] = [
  { id: "0", lat: 37.24054452695688, lng: -115.81196378347039, sequenceNumber: 0, doNotDisplayPin: true },
];
interface IRouteDetailsMapProps {}

export const RouteDetailsMap: FunctionComponent<IRouteDetailsMapProps> = (props) => {
  const { stops, moveStop, addStop, isInEditMode } = useContext(EditRouteStopsContext);

  const getMarkers = () => {
    const stopsWithBulks =
      stops.filter((stop) => stop.events?.find((event) => event.event_type === EventType.bulk)) ?? [];
    const firstBulk = lodash.minBy(stopsWithBulks, (stop) => stop.sequence_number)?.sequence_number;
    const lastBulk = lodash.maxBy(stopsWithBulks, (stop) => stop.sequence_number)?.sequence_number;
    const mappedMarkers = stops.map((stop) => {
      const marker: IMarker = {
        id: stop.id,
        lat: stop.latitude,
        lng: stop.longitude,
        sequenceNumber: stop.sequence_number,
        type: getMarkerType(stop, firstBulk, lastBulk),
      };

      return marker;
    });
    const result = mappedMarkers as IMarker[];
    return result.length ? result : fallBackMarker;
  };

  const getMarkerType = (
    stop: RouteStopFragment,
    sequenceNumberOfFirstBulk?: number,
    sequenceNumberOfLastBulk?: number,
  ): MarkerType => {
    if (sequenceNumberOfLastBulk != null && stop.sequence_number === sequenceNumberOfLastBulk) return "end";
    if (sequenceNumberOfFirstBulk != null && stop.sequence_number === sequenceNumberOfFirstBulk) return "start";
    if (!hasStopRelevantInfo(stop)) return "circle";
    return "default";
  };

  const hasStopRelevantInfo = (stop: RouteStopFragment) => {
    return stop.events && stop.events.some((event) => event.event_type === EventType.bulk && event.info);
  };

  const renderCustomInfoWindow = (id: string) => {
    const stop = stops.find((stop) => stop.id === id);
    if (!stop) {
      return "";
    }

    return <RouteDetailsMapInfoWindow stop={stop} />;
  };

  const renderCustomPolylines = useCallback(() => {
    const coords: PolygonlineCoords[] = [];
    for (let i = 0; i < stops.length; i++) {
      const currentStop = stops[i];
      const nextStop = stops[i + 1];

      if (!nextStop) {
        break;
      }

      const nextHasBackwardsEvent = nextStop.events?.some((event) => event.event_type === EventType.backwards);

      const lineCoords: PolygonlineCoords = {
        color: nextHasBackwardsEvent ? PolygonlineColor.RED : PolygonlineColor.BLACK,
        coords: [],
      };

      lineCoords.coords.push({ lat: currentStop.latitude, lng: currentStop.longitude });
      for (let y = i + 1; y < stops.length; y++) {
        const stop = stops[y];
        const followingStop = stops[y + 1];
        const followingHasBackwardsEvent = followingStop?.events?.some(
          (event) => event.event_type === EventType.backwards,
        );
        lineCoords.coords.push({ lat: stop.latitude, lng: stop.longitude });

        if (nextHasBackwardsEvent !== followingHasBackwardsEvent) {
          break;
        }
        i = i + 1;
      }

      coords.push(lineCoords);
    }

    return (
      <>
        {coords.map((data, index) => {
          return (
            <Polyline
              key={`polyline-${index}`}
              path={data.coords}
              options={{
                strokeColor: data.color,
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
            />
          );
        })}
      </>
    );
  }, [stops]);

  const handleMapClicked = (e: google.maps.MapMouseEvent) => {
    if (isInEditMode && e.latLng) {
      addStop(e.latLng.lat(), e.latLng.lng());
    }
  };

  const markers = getMarkers();
  return (
    <CustomMap
      height="450px"
      onMarkerMouseUp={(id: string, lat: number, lng: number) => moveStop(id, lat, lng)}
      isInEditMode={isInEditMode}
      markers={markers}
      displayPolyline={true}
      doRenderCustomInfoWindow={true}
      renderCustomInfoWindow={renderCustomInfoWindow}
      doRenderCustomPolylines={true}
      renderCustomPolylines={renderCustomPolylines}
      disableZoom={true}
      disableFitBounds={true}
      onMapClicked={handleMapClicked}
    />
  );
};
